'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _defineProperty = require('@babel/runtime/helpers/defineProperty');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var react = require('@chakra-ui/react');
var utils = require('@chakra-ui/utils');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
var _defineProperty__default = /*#__PURE__*/_interopDefaultLegacy(_defineProperty);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty__default["default"](target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
/**
 * Bridges `useFormControlProp` with `react-stately`'s and `react-aria`'s `useComponent` / `useComponentState` hooks.
 *
 * It takes user-provided props, passes them through `useFormControl` and returns objects that can be given to
 * `react-aria`'s and `react-stately`'s `useComponent` hooks.
 *
 * @example
 *   const { stateBridge, ariaBridge, htmlProps } = useFormControlBridge(props);
 *
 *   const state = useDateFieldState({
 *     ...stateBridge,
 *     ...otherProps
 *   });
 *
 *   const { fieldProps } = useDateField({
 *     ...ariaBridge,
 *     state
 *   });
 *
 *   return <div {{ ...fieldProps, ...htmlProps }} />
 *
 * @returns An object containing `stateBridge`, `ariaBridge` and `htmlProps`.
 */
function useFormControlBridge() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var formControlProps = react.useFormControlProps(props);
  var _ref = formControlProps || {},
    id = _ref.id,
    isReadOnly = _ref.isReadOnly,
    isDisabled = _ref.isDisabled,
    isRequired = _ref.isRequired,
    isInvalid = _ref.isInvalid;
  var _split = utils.split(props, ['id', 'onBlur', 'onFocus', 'isReadOnly', 'isDisabled', 'isRequired', 'isInvalid', 'aria-describedby', 'aria-labelledby']),
    _split2 = _slicedToArray__default["default"](_split, 2),
    nonFormControlProps = _split2[1];
  var formControlContext = react.useFormControlContext();
  var _ref2 = formControlContext || {},
    labelId = _ref2.labelId,
    hasFeedbackText = _ref2.hasFeedbackText,
    feedbackId = _ref2.feedbackId,
    helpTextId = _ref2.helpTextId,
    hasHelpText = _ref2.hasHelpText;
  var labelledBy = [props === null || props === void 0 ? void 0 : props['aria-labelledby'], labelId].filter(Boolean).join(' ');
  var describedBy = [props === null || props === void 0 ? void 0 : props['aria-describedby'], hasHelpText && helpTextId, hasFeedbackText && isInvalid && feedbackId].filter(Boolean).join(' ');
  var stateBridge = _objectSpread({
    isRequired: isRequired,
    isReadOnly: isReadOnly,
    isDisabled: isDisabled,
    validationState: isInvalid ? 'invalid' : 'valid'
  }, nonFormControlProps);
  var ariaBridge = _objectSpread({
    id: id,
    'aria-describedby': describedBy || undefined,
    'aria-labelledby': labelledBy || undefined
  }, nonFormControlProps);
  var htmlProps = {
    'aria-invalid': utils.ariaAttr(isInvalid),
    'aria-required': utils.ariaAttr(isRequired),
    'aria-readonly': utils.ariaAttr(isReadOnly)
  };
  var rest = _objectSpread(_objectSpread({}, nonFormControlProps), {}, {
    // We overwrite the events here because we want to use what `formControlProps` provides. If `<FormControl />`
    // didn't get any events, it will, under-the-hood, use the events from the given props. Otherwise, it will merge
    // the events from the given props with the events from `<FormControl />`.
    onBlur: formControlProps === null || formControlProps === void 0 ? void 0 : formControlProps.onBlur,
    onFocus: formControlProps === null || formControlProps === void 0 ? void 0 : formControlProps.onFocus
  });
  return _objectSpread({
    stateBridge: utils.filterUndefined(stateBridge),
    ariaBridge: utils.filterUndefined(ariaBridge),
    htmlProps: utils.filterUndefined(htmlProps)
  }, rest);
}
exports.useFormControlBridge = useFormControlBridge;